import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableCell,
  Typography,
} from '@mui/material';
import { HandlingUnitGroup, InventoryCountOrder } from '../../store/config/types';
import {
  AssignmentReturn,
  AssignmentTurnedIn,
  Close,
  Error,
  MoreHorizRounded,
  MoreVert,
  Person,
  Print,
  Store,
  SyncDisabled,
} from '@material-ui/icons';
import { convertConstantLiteralToNormal, formatDate, getUserDisplayName } from '../../utils/string.helpers';
import StatusChip from '../../components/StatusChip/StatusChip';
import CommentPreview from '../../components/Comments/CommentPreview';
import PieProgress from '../../components/PieProgress/PieProgress';
import { TableRow } from '@material-ui/core';
import './InventoryCountOrderDetails.scss';
import FilterableTable from '../../components/FilterableTable/FilterableTable';
import React, { ReactNode, useMemo, useState } from 'react';
import { InventoryCountOrderStatus } from '../../store/config/enums/warehouseWork.enum';

interface InventoryCountOrderDetailsProps {
  inventoryCountOrder?: InventoryCountOrder;
  disableActions?: boolean;
  lastRefresh?: Date;
  // eslint-disable-next-line no-unused-vars
  onAction: (action: string, data: any, targetObject?: any) => void;
}
function InventoryCountOrderDetails({
  inventoryCountOrder,
  disableActions,
  lastRefresh,
  onAction,
}: InventoryCountOrderDetailsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentHuGroup, setCurrentHuGroup] = useState<any>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setCurrentHuGroup(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentHuGroup(null);
  };

  const handleActionClick = (action: string) => {
    handleMenuClose();
    onAction(action, [{ ...currentHuGroup.operationIds }]);
  };
  const isApprovable = useMemo(() => {
    if (!inventoryCountOrder) return false;
    let result = true;
    result &&= inventoryCountOrder.status === InventoryCountOrderStatus.PENDING_APPROVAL;
    result &&=
      inventoryCountOrder.orderInfo?.items?.every(
        (item) => item.reviewCompleted === true || item.additionalData?.confirmZero,
      ) ?? false;
    return result;
  }, [inventoryCountOrder]);
  const handleHandlingUnitClick = (item: HandlingUnitGroup) => {
    onAction('display-handling-units', item.handlingUnits ?? []);
  };

  const isCounting = inventoryCountOrder
    ? ['NEW', 'PENDING', 'PROCESSING'].includes(inventoryCountOrder.status)
    : false;
  const canApprove = inventoryCountOrder?.status === InventoryCountOrderStatus.PENDING_APPROVAL;

  const handlePrint = () => {
    if (!inventoryCountOrder) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const doc = printWindow.document;
    doc.title = `Inventory Count Order - ${formatDate(inventoryCountOrder.date)}`;

    const style = doc.createElement('style');
    style.textContent = `
      @media print {
        @page { size: auto; margin: 15mm; }
        .no-print { display: none !important; }
      }
      body {
        font-family: Arial, Helvetica, sans-serif;
        margin: 20px;
        color: #333;
        font-size: 12px;
      }
      h1 { font-size: 18px; margin: 0 0 4px 0; }
      .header { margin-bottom: 16px; border-bottom: 2px solid #333; padding-bottom: 10px; }
      .header-info { display: flex; gap: 24px; color: #666; font-size: 12px; margin-top: 4px; }
      .header-info span { display: inline-flex; align-items: center; gap: 4px; }
      .status-badge {
        display: inline-block; padding: 2px 8px; border-radius: 10px;
        font-size: 11px; font-weight: bold; color: white; background: #666;
      }
      .product-section { margin-bottom: 16px; page-break-inside: avoid; }
      .product-header {
        background: #f5f5f5; padding: 8px 12px; border-left: 4px solid #1976d2;
        margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;
      }
      .product-name { font-weight: bold; font-size: 14px; }
      .product-desc { color: #888; font-size: 11px; }
      .product-summary { text-align: right; font-size: 12px; }
      .product-summary div { margin: 2px 0; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th { background: #e0e0e0; padding: 6px 8px; text-align: left; font-weight: bold; border: 1px solid #ccc; }
      td { padding: 5px 8px; border: 1px solid #ddd; }
      tr:nth-child(even) { background: #fafafa; }
      .text-right { text-align: right; }
      .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 10px; color: #999; }
    `;
    doc.head.appendChild(style);

    const statusColor = (status: string) => {
      const s = status?.toUpperCase();
      if (s === 'APPROVED' || s === 'SYNCED') return '#4caf50';
      if (s === 'REJECTED') return '#f44336';
      if (s === 'PENDING_APPROVAL') return '#ff9800';
      if (s === 'PROCESSING') return '#2196f3';
      return '#9e9e9e';
    };

    const items = inventoryCountOrder.orderInfo?.items ?? [];

    const productSections = items
      .map((item) => {
        const huGroups = (item.handlingUnitGroups ?? []).filter((huGroup) => !huGroup.additionalData?.systemDefined);

        const rows = huGroups
          .map((huGroup) => {
            const difference = (huGroup.quantityCounted ?? 0) - (huGroup.quantityExpected ?? 0);
            const discrepancy = (huGroup.rate ?? 0) * difference;
            const unit = huGroup.unit ?? item.product?.unitOfMeasure ?? '';
            return `
          <tr>
            <td>${huGroup.type === 'LOT' ? convertConstantLiteralToNormal(huGroup.type) : ''}</td>
            <td>${huGroup.type === 'LOT' ? (huGroup.displayName ?? '') : ''}</td>
            <td class="text-right">${(huGroup.quantityCounted ?? 0).toFixed(2)} ${unit}</td>
            <td class="text-right">${(huGroup.quantityExpected ?? 0).toFixed(2)} ${unit}</td>
            <td class="text-right">${isCounting ? '-' : difference.toFixed(2)}</td>
            <td class="text-right">${isCounting ? '-' : discrepancy.toFixed(2)}</td>
            <td>${convertConstantLiteralToNormal(huGroup.status ?? 'NEW')}</td>
          </tr>
        `;
          })
          .join('');

        return `
        <div class="product-section">
          <div class="product-header">
            <div>
              <div class="product-name">${item.product?.name ?? 'Unknown Product'}</div>
              <div class="product-desc">${item.product?.description ?? ''}</div>
            </div>
            <div class="product-summary">
              <div>Counted: <strong>${(item.totalQuantityCounted ?? 0).toFixed(2)}</strong> ${item.product?.unitOfMeasure ?? ''}</div>
              <div>Expected: <strong>${(item.totalQuantityExpected ?? 0).toFixed(2)}</strong> ${item.product?.unitOfMeasure ?? ''}</div>
            </div>
          </div>
          ${
            huGroups.length > 0
              ? `
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>ID</th>
                  <th class="text-right">Counted</th>
                  <th class="text-right">Expected</th>
                  <th class="text-right">Difference</th>
                  <th class="text-right">Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          `
              : '<p style="color:#999; font-style:italic;">No handling unit groups</p>'
          }
        </div>
      `;
      })
      .join('');

    doc.body.innerHTML = `
      <div class="header">
        <h1>Inventory Count Order</h1>
        <div class="header-info">
          <span>Date: ${formatDate(inventoryCountOrder.date)}</span>
          <span><span class="status-badge" style="background:${statusColor(inventoryCountOrder.status)}">${convertConstantLiteralToNormal(inventoryCountOrder.status ?? '')}</span></span>
          <span>User: ${getUserDisplayName(inventoryCountOrder.warehouseUser)}</span>
          <span>Warehouse: ${inventoryCountOrder.warehouse?.name ?? ''}</span>
        </div>
      </div>
      ${productSections}
      <div class="footer">Printed on ${new Date().toLocaleString()}</div>
    `;

    const script = doc.createElement('script');
    script.textContent = 'window.onload = function() { window.print(); };';
    doc.body.appendChild(script);
  };

  if (!inventoryCountOrder) return <></>;
  const NotProvided: React.FC = () => <MoreHorizRounded style={{ color: 'gray' }} />;
  function handleCumulativeOperation(operation: string, rows: any[]): void {
    if (onAction) {
      onAction(
        operation,
        rows.map((row) => row.operationIds),
      );
    }
  }
  return (
    <Grid container flexDirection={'column'} p={4} pt={1} gap={2}>
      <Grid item>
        <Grid container alignItems={'center'}>
          <Grid item flexGrow={1}>
            <Grid container gap={1} alignItems={'center'}>
              <Grid>Date: {formatDate(inventoryCountOrder.date)}</Grid>
              <Grid>
                <StatusChip status={inventoryCountOrder.status ?? ''} />
              </Grid>
              <Grid>
                <Grid container alignItems="flex-end">
                  <Person /> {getUserDisplayName(inventoryCountOrder.warehouseUser)}
                </Grid>
              </Grid>
              <Grid display="none">{lastRefresh?.getTime()}</Grid>
            </Grid>
          </Grid>
          <Grid item>
            <IconButton onClick={handlePrint} title="Print">
              <Print />
            </IconButton>
            <IconButton
              onClick={() => {
                onAction('close-order-tabs', [inventoryCountOrder.inventoryCountOrderId]);
              }}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container alignItems={'flex-end'} gap={1}>
          <Store /> {inventoryCountOrder.warehouse?.name}
        </Grid>
      </Grid>

      <Grid item>
        {(inventoryCountOrder.orderInfo?.items ?? []).map((item) => (
          <Accordion key={item.productId}>
            <AccordionSummary>
              <Grid container gap={1} alignItems={'center'} flexWrap={'nowrap'}>
                <Grid item mr={1}>
                  <PieProgress value={item.progress ?? 0} size={32} />
                </Grid>
                <Grid item flexGrow={1}>
                  <Typography variant="body1">{item.product?.name}</Typography>
                  <Typography variant="caption" color="gray">
                    {item.product?.description}
                  </Typography>
                </Grid>
                <Grid item>
                  <Table className="quantity-info">
                    <TableRow className="quantity-info-row">
                      <TableCell className="quantity-info-cell quantity-info-caption">Counted</TableCell>
                      <TableCell className="quantity-info-cell quantity-info-value">
                        <div> {(item.totalQuantityCounted ?? 0).toFixed(2)}</div>
                      </TableCell>
                      <TableCell className="quantity-info-cell quantity-info-caption">
                        {item.product?.unitOfMeasure}
                      </TableCell>
                    </TableRow>
                    <TableRow className="quantity-info-row">
                      <TableCell className="quantity-info-cell quantity-info-caption">Expected</TableCell>
                      <TableCell className="quantity-info-cell quantity-info-value">
                        {(item.totalQuantityExpected ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="quantity-info-cell quantity-info-caption">
                        {item.product?.unitOfMeasure}
                      </TableCell>
                    </TableRow>
                  </Table>
                </Grid>
                <Grid item width={200}>
                  <CommentPreview
                    style={{ maxWidth: 200 }}
                    entityInfo={{
                      entity: `inventoryCount-${inventoryCountOrder.inventoryCountOrderId}`,
                      entityId: item.product?.productId ?? 0,
                    }}
                    onClick={(thread, callback) => onAction('open-comments-dialog', [], { thread, callback })}
                  />
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid p={2} pt={0}>
                <FilterableTable
                  selectable
                  cumulativeOperations={[
                    {
                      operation: 'inventory-count-approve-row',
                      caption: 'Approve Selected',
                      icon: <AssignmentTurnedIn />,
                      isDisabled(rows) {
                        // return !rows.some((r) => !r.status || r.status == 'NEW');
                        if (!canApprove) return true;
                        return !rows.length;
                      },
                    },
                    {
                      operation: 'inventory-count-reject-row',
                      caption: 'Reject Selected',
                      icon: <AssignmentReturn />,
                      isDisabled(rows) {
                        // return !rows.some((r) => !r.status || r.status == 'NEW');
                        if (!canApprove) return true;
                        return !rows.length;
                      },
                    },
                    {
                      operation: 'inventory-count-clear-row',
                      caption: 'Clear Selected statuses',

                      isDisabled(rows) {
                        if (!canApprove) return true;
                        return !rows.length || rows.some((r) => !r.status || r.status == 'NEW');
                      },
                    },
                  ]}
                  onCumulativeOperation={handleCumulativeOperation}
                  primaryKey="index"
                  data={
                    item.handlingUnitGroups
                      ?.filter((huGroup) => !huGroup.additionalData?.systemDefined)
                      .map((huGroup, index) => {
                        const difference = (huGroup.quantityCounted ?? 0) - (huGroup.quantityExpected ?? 0);
                        return {
                          ...huGroup,
                          index,
                          difference,
                          discrepancy: (huGroup.rate ?? 0) * difference,
                          humanizedStatus: convertConstantLiteralToNormal(huGroup.status ?? 'NEW'),
                          unit: huGroup.unit ?? item.product?.unitOfMeasure,
                          operationIds: {
                            inventoryCountOrderId: inventoryCountOrder.inventoryCountOrderId,
                            productId: item.productId,
                            handlingUnitGroupId: huGroup.identificationData,
                          },
                        };
                      }) ?? []
                  }
                  columns={[
                    {
                      field: 'type',
                      caption: 'Type',
                      render(row) {
                        return row.type == 'LOT' ? convertConstantLiteralToNormal(row.type) : '';
                      },
                    },
                    {
                      field: 'displayName',
                      caption: 'ID',
                      sortable: true,
                      filterType: 'string',
                      render(row) {
                        return row.type == 'LOT' ? row.displayName : '';
                      },
                    },
                    {
                      field: 'quantityCounted',
                      caption: 'Counted',
                      sortable: true,
                      filterType: 'number',
                      render(row) {
                        if (row.quantityCounted === undefined) return <NotProvided />;
                        return (
                          <Grid container alignItems={'center'} className="quantity-info">
                            <div
                              className="anchor quantity-info-row"
                              style={{ display: 'inline' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHandlingUnitClick(row);
                              }}
                            >
                              <span className="quantity-info-value">{(row.quantityCounted ?? 0).toFixed(2)}</span>
                              <span className="quantity-info-caption">{row.unit}</span>
                            </div>
                          </Grid>
                        );
                      },
                    },
                    {
                      field: 'quantityExpected',
                      caption: 'Expected',
                      sortable: true,
                      filterType: 'number',
                      render(row) {
                        if (row.quantityExpected === undefined) return <NotProvided />;
                        return (
                          <Grid container alignItems={'center'} className="quantity-info">
                            <div className="quantity-info-row" style={{ display: 'inline' }}>
                              <span className="quantity-info-value">{(row.quantityExpected ?? 0).toFixed(2)}</span>
                              <span className="quantity-info-caption">{row.unit}</span>
                            </div>
                          </Grid>
                        );
                      },
                    },
                    {
                      field: 'difference',
                      caption: 'Difference',
                      sortable: true,
                      filterType: 'number',
                      render(row) {
                        if (isCounting) return <NotProvided />;
                        return (row.difference ?? 0).toFixed(2);
                      },
                    },
                    {
                      field: 'discrepancy',
                      caption: 'Amount',
                      sortable: true,
                      filterType: 'number',
                      render(row) {
                        if (isCounting) return <NotProvided />;
                        return (row.discrepancy ?? 0).toFixed(2);
                      },
                    },
                    {
                      field: 'humanizedStatus',
                      caption: ' ',
                      sortable: true,
                      filterType: 'values',
                      render(row) {
                        // if (row.status === undefined) return <NotProvided />;
                        return (
                          <Grid container gap={1} alignItems={'center'} flexWrap={'nowrap'}>
                            <StatusChip status={row.status ?? 'NEW'} small />
                            {inventoryCountOrder.status == 'PENDING_APPROVAL' && (
                              <IconButton size="small" onClick={(e) => handleMenuClick(e, row)}>
                                <MoreVert />
                              </IconButton>
                            )}
                          </Grid>
                        );
                      },
                    },
                  ]}
                />
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
      <ErrorAlert inventoryCountOrder={inventoryCountOrder} />
      <Grid item>
        <Grid container justifyContent={'end'} gap={2}>
          {['PROCESSING', 'PENDING_APPROVAL', 'REJECTED'].includes(inventoryCountOrder.status) && (
            <Button
              color="info"
              variant="outlined"
              disabled={disableActions}
              onClick={() => {
                if (onAction) onAction('inventory-count-order-refresh-snapshot', [inventoryCountOrder]);
              }}
            >
              Refresh Snapshot
            </Button>
          )}
          {inventoryCountOrder.status == 'PENDING_APPROVAL' && (
            <>
              <Button
                color="error"
                variant="outlined"
                disabled={disableActions}
                onClick={() => {
                  if (onAction) onAction('inventory-count-order-reject', [inventoryCountOrder]);
                }}
              >
                Reject & REOPEN
              </Button>
              <Button
                color="success"
                variant="outlined"
                disabled={disableActions || !isApprovable}
                onClick={() => {
                  if (onAction) onAction('inventory-count-order-approve', [inventoryCountOrder]);
                }}
              >
                Approve
              </Button>
            </>
          )}
          {inventoryCountOrder.status == 'APPROVED' && (
            <Grid container alignItems="center" gap={1}>
              <Grid item flexGrow={1}>
                <Alert variant="standard" color="warning" icon={<AssignmentTurnedIn />}>
                  <a
                    href={inventoryCountOrder.additionalData.externalLink}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    Note: Before syncing click here to to start the inventory count record in NetSuite.
                  </a>
                </Alert>
              </Grid>
              <Grid item>
                <Button
                  color="success"
                  variant="outlined"
                  disabled={disableActions}
                  onClick={() => {
                    if (onAction) onAction('inventory-count-order-try-sync', [inventoryCountOrder]);
                  }}
                >
                  Sync
                </Button>
              </Grid>
            </Grid>
          )}
          {inventoryCountOrder.status == 'SYNC_FAILED' && (
            <Grid container flexDirection="column" gap={1}>
              <Grid item flexGrow={1}>
                {inventoryCountOrder.orderInfo?.errors?.map((error: string, errIndex: number) => (
                  <Alert variant="standard" color="error" key={errIndex} title="test" icon={<SyncDisabled />}>
                    {error}
                  </Alert>
                ))}
              </Grid>
              <Grid item>
                <Grid container justifyContent="end">
                  <Button
                    color="warning"
                    variant="outlined"
                    disabled={disableActions}
                    onClick={() => {
                      if (onAction) onAction('inventory-count-order-try-sync', [inventoryCountOrder]);
                    }}
                  >
                    Retry Sync
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {!currentHuGroup?.status || currentHuGroup.status == 'NEW' ? (
          <>
            <MenuItem onClick={() => handleActionClick('inventory-count-approve-row')}>Approve</MenuItem>
            <MenuItem onClick={() => handleActionClick('inventory-count-reject-row')}>Reject</MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => handleActionClick('inventory-count-clear-row')}>Clear</MenuItem>
          </>
        )}
      </Menu>
    </Grid>
  );
}

export default React.memo(InventoryCountOrderDetails, (prevProps, nextProps) => {
  let eq =
    prevProps.inventoryCountOrder?.inventoryCountOrderId === nextProps.inventoryCountOrder?.inventoryCountOrderId;
  // eq &&= prevProps.disableActions === nextProps.disableActions;
  eq &&= prevProps.lastRefresh?.valueOf() === nextProps.lastRefresh?.valueOf();
  return eq;
});

function ErrorAlert({
  inventoryCountOrder,
  icon = <Error />,
}: {
  inventoryCountOrder: InventoryCountOrder;
  icon?: ReactNode;
}) {
  if (!inventoryCountOrder.additionalData?.errors?.length || !Array.isArray(inventoryCountOrder.additionalData?.errors))
    return <></>;
  return (
    <Grid>
      {inventoryCountOrder.additionalData?.errors?.map((error: string, errIndex: number) => (
        <Alert variant="standard" color="error" key={errIndex} title="test" icon={icon}>
          {error}
        </Alert>
      ))}
    </Grid>
  );
}
